import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './lightbox-thumbnails.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import type { LightboxImageFitType, LightboxMediaItem } from 'components/discovery/pdp/lightbox/interfaces.js'

initializeIconsByRole({
  'csc-lightbox-thumbnails.play': { name: 'play', style: 'solid' },
})

export const thumbnailOrientations = ['horizontal', 'vertical'] as const
export type ThumbnailOrientationType = typeof thumbnailOrientations[number]

export const thumbnailAlignments = ['start', 'center', 'stretch'] as const
export type ThumbnailAlignmentType = typeof thumbnailAlignments[number]

const nearestScrollOffset = (scrollPosition: number, itemStart: number, itemSize: number, viewportSize: number): number => {
  if (itemStart < 0) return scrollPosition + itemStart
  if (itemStart + itemSize > viewportSize) return scrollPosition + itemStart + itemSize - viewportSize
  return scrollPosition
}

@customElement('csc-lightbox-thumbnails')
export class CSCLightboxThumbnails extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Array })
  images: LightboxMediaItem[] = []

  @property({ type: Number, attribute: 'active-index' })
  activeIndex = 0

  @property({ type: String, reflect: true })
  orientation: ThumbnailOrientationType = 'horizontal'

  @property({ type: String, reflect: true })
  alignment: ThumbnailAlignmentType = 'start'

  @property({ type: String, attribute: 'image-fit' })
  imageFit: LightboxImageFitType = 'cover'

  @query('.csc-lightbox-thumbnails')
  private container!: HTMLElement

  @state()
  private showLeadingFade = false

  @state()
  private showTrailingFade = false

  private resizeObserver?: ResizeObserver

  override connectedCallback() {
    super.connectedCallback()
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollFade()
    })
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.resizeObserver?.disconnect()
  }

  override firstUpdated() {
    this.resizeObserver?.observe(this)
    this.resizeObserver?.observe(this.container)
    requestAnimationFrame(() => {
      this.updateScrollFade()
    })
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)
    if (
      changedProperties.has('images')
      || changedProperties.has('activeIndex')
      || changedProperties.has('alignment')
      || changedProperties.has('orientation')
    ) {
      requestAnimationFrame(() => {
        this.updateScrollFade()
      })
    }

    if (changedProperties.has('activeIndex')) {
      requestAnimationFrame(() => {
        this.scrollActiveIntoView()
      })
    }
  }

  private scrollActiveIntoView() {
    const container = this.container
    const active = container?.querySelector<HTMLElement>('.thumbnail.active')
    if (!container || !active) return

    const containerRect = container.getBoundingClientRect()
    const activeRect = active.getBoundingClientRect()

    container.scrollTo({
      left: nearestScrollOffset(container.scrollLeft, activeRect.left - containerRect.left, activeRect.width, container.clientWidth),
      top: nearestScrollOffset(container.scrollTop, activeRect.top - containerRect.top, activeRect.height, container.clientHeight),
      behavior: 'smooth',
    })
  }

  private onScroll() {
    this.updateScrollFade()
  }

  private updateScrollFade() {
    if (!this.container) {
      this.showLeadingFade = false
      this.showTrailingFade = false
      return
    }

    if (this.orientation === 'vertical') {
      this.updateVerticalScrollFade()
      return
    }

    this.updateHorizontalScrollFade()
  }

  private updateHorizontalScrollFade() {
    const containerRect = this.container.getBoundingClientRect()
    const thumbnails = Array.from(this.container.querySelectorAll('.thumbnail'))

    if (thumbnails.length === 0) {
      this.showLeadingFade = false
      this.showTrailingFade = false
      return
    }

    this.showLeadingFade = thumbnails.some(
      (thumbnail) => thumbnail.getBoundingClientRect().left < containerRect.left - 1,
    )
    this.showTrailingFade = thumbnails.some(
      (thumbnail) => thumbnail.getBoundingClientRect().right > containerRect.right + 1,
    )
  }

  private updateVerticalScrollFade() {
    const containerRect = this.container.getBoundingClientRect()
    const thumbnails = Array.from(this.container.querySelectorAll('.thumbnail'))

    if (thumbnails.length === 0) {
      this.showLeadingFade = false
      this.showTrailingFade = false
      return
    }

    this.showLeadingFade = thumbnails.some(
      (thumbnail) => thumbnail.getBoundingClientRect().top < containerRect.top - 1,
    )
    this.showTrailingFade = thumbnails.some(
      (thumbnail) => thumbnail.getBoundingClientRect().bottom > containerRect.bottom + 1,
    )
  }

  private onThumbnailClick(index: number) {
    this.dispatchEvent(new CustomEvent('thumbnail-click', {
      detail: { index },
      bubbles: true,
      composed: true
    }))
  }

  private renderThumbnail(item: LightboxMediaItem, index: number) {
    const isVideo = item.kind === 'video'

    return html`
      <div
        class="${classMap({
          'thumbnail': true,
          'active': index === this.activeIndex,
          'is-video': isVideo,
        })}"
        @click="${() => this.onThumbnailClick(index)}"
      >
        ${isVideo && item.source ? html`
          <cse-image
            source="${item.source}"
            alt="${item.alt}"
            fit="cover"
            loading="lazy"
          ></cse-image>
        ` : isVideo ? html`
          <div class="video-placeholder" aria-hidden="true"></div>
        ` : html`
          <cse-image
            source="${item.thumbnailSource || item.source}"
            alt="${item.alt}"
            fit="${this.imageFit}"
            loading="lazy"
            fallback-source="${item.fallbackSource ?? nothing}"
          ></cse-image>
        `}
        ${isVideo ? html`
          <div class="video-overlay" aria-hidden="true">
            <cse-icon icon-role="csc-lightbox-thumbnails.play" size="sm"></cse-icon>
          </div>
        ` : nothing}
      </div>
    `
  }

  override render() {
    if (this.images.length === 0) return nothing

    return html`
      <div class="${classMap({
        'thumbnails-wrapper': true,
        [`orientation-${this.orientation}`]: true,
        [`alignment-${this.alignment}`]: true,
      })}">
        <div class="thumbnails-viewport">
          <div
            class="${classMap({
              'csc-lightbox-thumbnails': true,
              [`orientation-${this.orientation}`]: true,
            })}"
            @scroll="${this.onScroll}"
          >
            ${this.images.map((image, index) => this.renderThumbnail(image, index))}
          </div>
          ${this.showLeadingFade ? html`
            <div
              class="${this.orientation === 'vertical' ? 'scroll-fade-top' : 'scroll-fade-left'}"
              aria-hidden="true"
            ></div>
          ` : nothing}
          ${this.showTrailingFade ? html`
            <div
              class="${this.orientation === 'vertical' ? 'scroll-fade-bottom' : 'scroll-fade-right'}"
              aria-hidden="true"
            ></div>
          ` : nothing}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-lightbox-thumbnails': CSCLightboxThumbnails
  }
}
